import { AssignmentDTO, AssignmentDTOClient } from 'models/assignment'
import { AssignmentJob, AssignmentStage } from 'constants/assignment'
import { BEIGE_600, GRAY_900, MOBILE_VIEW_WIDTH_QUERY, WHITE } from 'constants/styling/theme'
import { Box, Grid, Typography, useMediaQuery } from '@mui/material'
import { FC, Fragment, useCallback, useMemo } from 'react'
import { PRODUCTS_WITH_OPTIONAL_STAGING, ProductKindsWithDocumentGallery, STAGING_PRODUCT_KINDS } from 'constants/product'

import { CircleIcon } from 'components/common/CircleIcon'
import { ClientGalleryStagingTypeFilter } from './ClientGalleryStagingTypeFilter.module'
import FilterTab from 'components/common/FilterTab/FilterTab.module'
import { MUIBadge } from 'components/common/MUIBadge'
import { ProductDTO } from 'models/product'
import { RealEstatePropertyAssignmentDTO, RealEstatePropertyAssignmentProductsDTO } from 'models/property'
import { choosePrimaryProduct } from 'utils/product'
import { getProductKindIcon } from 'utils/helpers/getProductKindIcon'
import { getProgressCategoryData } from 'components/common/Gallery/AssignmentProgress/AssignmentProgressSteps.utils'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import { groupPropertyAssignmentsWithStaging } from 'utils/helpers/groupPropertyAssignmentsWithStaging'
import { isShootingCategory } from 'utils/validators'
import { useClientGallery } from '../_main/contexts'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

/**
 * Union of the product shapes an assignment tab can carry. Client/deal assignments expose
 * `ProductDTO` (with `isPrimary`), while property assignments expose the leaner
 * `RealEstatePropertyAssignmentProductsDTO` (no `isPrimary`, so it falls back to the first product).
 */
type AssignmentTabProduct = ProductDTO | RealEstatePropertyAssignmentProductsDTO

/**
 * @interface Props
 */
interface Props {
  /** List of missions */
  missions: AssignmentDTOClient[] | RealEstatePropertyAssignmentDTO[]
  /** Tab click event */
  onClick?: (id: string) => void
  /** Is non-document product */
  isNonDocumentProduct?: boolean
  /** Deal assignments for tab */
  dealAssignmentsForTab: AssignmentDTO[] | RealEstatePropertyAssignmentDTO[]
}

/**
 * Client gallery order type filters.
 * 
 * @example <ClientGalleryOrderTypeFilter />
 */
export const ClientGalleryOrderTypeFilter: FC<Props> = ({
  missions,
  onClick,
  isNonDocumentProduct = false,
  dealAssignmentsForTab,
}) => {
  const { pathname } = useLocation()
  const { t } = useTranslation(['gallery'])
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const { enableStagingFlowGallery } = useClientGallery()

  const { assignmentId } = useClientGallery()

  const isPropertyPathname = useMemo(() => getPropertyPathname(pathname), [pathname])

  const badgeColor = useCallback((status?: string) => {
    if (status === AssignmentJob.ORDER_DELIVERED_STAGING_ACTION_IN_PROGRESS) return 'blue'
    if (status === AssignmentJob.ASSIGNMENT_CANCELLED) return 'coral'
    else if (status !== AssignmentJob.ORDER_DELIVERED) return 'orange'

    return 'green'
  }, [])

  const productName = useCallback((assignment: AssignmentDTOClient | RealEstatePropertyAssignmentDTO) => {
    return assignment.products.map((product) => t(`product:p_${product.id}`)).join(', ')
  }, [t])

  /**
   * Comparator function for sorting by product kind.
   * Sorts staging products based on their kind, prioritizing certain types to appear first.
   * Staging products matching STAGING_PRODUCT_KINDS are sorted to the end of the array.
   */
  const sortByKind = (a: AssignmentDTOClient | RealEstatePropertyAssignmentDTO, b: AssignmentDTOClient | RealEstatePropertyAssignmentDTO): number => {
    // Retrieve the KIND values of the compared objects' primary products
    const kindA = choosePrimaryProduct<AssignmentTabProduct>(a.products)?.kind
    const kindB = choosePrimaryProduct<AssignmentTabProduct>(b.products)?.kind

    // Check if the kinds match the staging types defined in STAGING_PRODUCT_KINDS
    const isKindAStaging = !!kindA && STAGING_PRODUCT_KINDS.has(kindA)
    const isKindBStaging = !!kindB && STAGING_PRODUCT_KINDS.has(kindB)

    // Prioritize staging products to appear on the end of the sorted array
    if (isKindAStaging && !isKindBStaging) return 1
    else if (!isKindAStaging && isKindBStaging) return -1
    else return 0
  }

  const groupedAssignmentsWithStaging = useMemo(() => {
    if ((dealAssignmentsForTab as (RealEstatePropertyAssignmentDTO[] | AssignmentDTO)[]).every((assignment) => 'orderId' in assignment)
      && (dealAssignmentsForTab as (RealEstatePropertyAssignmentDTO[] | AssignmentDTO)[]).every((assignment) => 'primaryProduct' in assignment)) {
      // Check if there are deal assignments with additional staging and group them under order id
      return groupPropertyAssignmentsWithStaging(dealAssignmentsForTab as RealEstatePropertyAssignmentDTO[])
    }
    return null
  }, [dealAssignmentsForTab])

  const filterTabList = useMemo(() => {
    // Copied so the query cache is never sorted; the stable sort keeps the caller's shooting-start order within each kind
    const sortedData = [...missions].sort(sortByKind)

    return sortedData?.map((assignment) => {
      const primaryProduct = choosePrimaryProduct<AssignmentTabProduct>(assignment.products)

      if (!primaryProduct) return null

      const productKind = primaryProduct.kind
      const productCategory = primaryProduct.category

      // Skip additional staging filters if they are either:
      // 1. Not yet delivered (stage not VISUALS_SENT_TO_CLIENT)
      // 2. Related to the shooting category and have no active GROUND PHOTO assignment
      if (
        dealAssignmentsForTab.some((tabAssignment) => {
          const tabPrimaryProduct = choosePrimaryProduct<AssignmentTabProduct>(tabAssignment.products)

          return !!tabPrimaryProduct && PRODUCTS_WITH_OPTIONAL_STAGING.has(tabPrimaryProduct.kind)
        })
        && STAGING_PRODUCT_KINDS.has(productKind)
        && isShootingCategory(productCategory)
        && assignment.stage !== AssignmentStage.VISUALS_SENT_TO_CLIENT
      )
        return null

      // Remove isNonDocumentProduct handle after property feature is released and opened. Added this for remaining the old behavior.
      if (isNonDocumentProduct && ProductKindsWithDocumentGallery.has(productKind)) return null

      const isAssignmentWithAnyStagingAction = Boolean(groupedAssignmentsWithStaging?.find((order) =>
        order.assignments.some((assignmentWithStaging) => assignmentWithStaging.id === assignment.id && assignment.stage === AssignmentStage.VISUALS_SENT_TO_CLIENT)))

      const hasAllStagingProductsSentToEditor = Boolean(groupedAssignmentsWithStaging?.find((order) =>
        order.assignments
          .filter((assignmentWithStaging) =>
            STAGING_PRODUCT_KINDS.has(assignmentWithStaging.primaryProduct?.kind))
          .every((stagingAssignment) =>
            stagingAssignment.stage === AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR)
      ))

      // Check if the assignment has any stagings not sent to the editor - todo
      const isAssignmentWithStagingTodo = isAssignmentWithAnyStagingAction && !hasAllStagingProductsSentToEditor

      // Check if the assignment has all stagings sent to the editor (all are marked as VISUALS_SENT_BY_BKBN_TO_EDITOR) - then the Photo product will show as in progress
      const isAssignmentWithAllStagingsInProgress = isAssignmentWithAnyStagingAction && hasAllStagingProductsSentToEditor
      // Check if the assignment has all stagings done (all stagings are VISUALS_SENT_TO_CLIENT) - then the Photo product will show as done
      const isAssignmentWithAllStagingsDone = Boolean(groupedAssignmentsWithStaging?.find((order) =>
        order.assignments
          .filter((assignmentWithStaging) =>
            STAGING_PRODUCT_KINDS.has(assignmentWithStaging.primaryProduct?.kind))
          .every((stagingAssignment) =>
            stagingAssignment.stage === AssignmentStage.VISUALS_SENT_TO_CLIENT)
      ))

      const steps = getProgressCategoryData(assignment.stage, assignment.products, isAssignmentWithStagingTodo, isAssignmentWithAllStagingsInProgress, isAssignmentWithAllStagingsDone, assignment.isCancelled)
      const activeStepData = steps?.find((step) => step.activeStep)

      return (
        <FilterTab
          key={assignment.id}
          height='5.6rem'
          isActive={assignmentId === assignment.id}
          icon={
            <CircleIcon
              size="2.4rem"
              circleColor={assignmentId !== assignment.id ? BEIGE_600 : WHITE}
              icon={getProductKindIcon(productKind)}
            />
          }
          label={
            <Typography
              variant="text-sm"
              fontWeight="semiBold"
              color={GRAY_900}
              title={productName(assignment)}
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                lineClamp: '2',
                WebkitBoxOrient: 'vertical',
              }}
            >
              {productName(assignment)}
            </Typography>
          }
          badge={
            !!activeStepData &&

            <MUIBadge
              label={t(`order_informations.stages.${activeStepData?.jobDone}`)}
              size="sm"
              color={badgeColor(activeStepData.jobDone)}
            />
          }
          sx={{
            maxWidth: '35rem',
            flexShrink: 0
          }}
          onClick={() => onClick?.(assignment.id.toString())}
        />

      )
    })
    // t, navigate skipped from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, dealAssignmentsForTab, productName, badgeColor])

  return (
    <Fragment>
      {isMobileView ?
        <Box
          width="100vw"
          display="flex"
          gap=".8rem"
          overflow="auto"
          padding="3.2rem 0 0"
          boxSizing="border-box"
          sx={{
            '::-webkit-scrollbar': {
              display: 'none'
            }
          }}
        >
          {filterTabList}
          {(enableStagingFlowGallery && !isPropertyPathname) && <ClientGalleryStagingTypeFilter />}
        </Box>
        :
        <Grid container marginTop="3.2rem" gap=".8rem">
          {filterTabList}
          {(enableStagingFlowGallery && !isPropertyPathname) && <ClientGalleryStagingTypeFilter />}
        </Grid>
      }
    </Fragment>
  )
}
